import {lazy, useState} from 'react'
import {Navigate, useRouteError} from 'react-router'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {ChromelessErrorModal} from '@freckle/educator-materials/ts/components/chromeless-error-modal'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import * as Bugsnag from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'
import {displayNotification} from '@freckle/classroom/ts/common/helpers/helpers'
import {showErrorHandlerAndFail} from '@freckle/classroom/ts/common/routers/app-router'
import {Routes} from '@freckle/classroom/ts/common/routers/routes'
import {UserVisiblePageError} from '@freckle/classroom/ts/errors/user-visible-page-error'
import {useAccessedChromeless} from '@freckle/classroom/ts/hooks/chromeless/access'
import {useChromeless} from '@freckle/classroom/ts/hooks/chromeless/hook'
import {
  mkFailureToLoad,
  postMessageToWildcard,
  useChromelessPostMessage
} from '@freckle/classroom/ts/hooks/use-chromeless-post-message'

export const SignUpPageHandler = lazy(() => import('./../../components/page/signup-page'))

export const LoginPageHandler = lazy(() => import('./../../components/page/login-page/login'))

export const SsoMismatchedSchoolHandler = lazy(
  () => import('./../../components/page/sso-mismatched-school')
)

export const ChangePasswordPageHandler = lazy(
  () => import('./../../components/page/change-password-page/handler')
)

export const DeletedLoginHandler = lazy(() => import('./../../../pages/deleted-login'))

export const EmailConfirmed = lazy(() => import('./../../../pages/email-confirmed'))

export const InvalidLink = lazy(() => import('./../../../pages/invalid-link'))

/**
 * Error boundary for React Router's errorElement.
 * Uses useRouteError() to get the error caught by React Router.
 * Can redirect to dashboard since we're inside the router.
 */
export function ErrorRootBoundary() {
  const {t} = useSafeTranslation()
  const [errorHandled, setErrorHandled] = useState(false)
  const chromeless = useChromeless()
  const accessedChromeless = useAccessedChromeless()
  const chromelessPostMessage = useChromelessPostMessage()
  const error = useRouteError()

  useSafeEffectExtraDeps<{
    chromelessPostMessage: typeof chromelessPostMessage
    error: unknown
    t: typeof t
  }>(
    ({chromelessPostMessage, error, t}) => {
      if (error instanceof Error) {
        Bugsnag.logError(error)
      }

      if (error instanceof UserVisiblePageError && error.chromelessPostMessage !== undefined) {
        postMessageToWildcard({message: error.chromelessPostMessage})
      } else {
        chromelessPostMessage({
          message: mkFailureToLoad(),
          chromeAlternative: () =>
            showErrorHandlerAndFail('Error', 'Something went wrong. Please try again later.')
        })
      }

      if (!chromeless) {
        const errorMsg = error instanceof Error ? error.message : t('COMMON_ERROR_MESSAGE')
        displayNotification('error', errorMsg)
      }

      setErrorHandled(true)
    },
    [chromeless],
    {
      chromelessPostMessage: staticExtra(chromelessPostMessage),
      error: staticExtra(error),
      t: staticExtra(t)
    }
  )

  if (chromeless || accessedChromeless) {
    const title =
      error instanceof UserVisiblePageError ? error.title : t('CHROMELESS_GENERIC_ERROR_TITLE')
    const hideChrome = error instanceof UserVisiblePageError && error.hideChrome
    return <ChromelessErrorModal title={title} hideChrome={hideChrome} />
  }

  if (errorHandled) {
    return <Navigate to={Routes.dashboard()} replace />
  }

  return null
}
